import {Icon} from '@/app/components/Icon';
import {useMemo, useRef} from 'react';
import {Link, useMatches, useNavigate} from 'react-router-dom';
import {Breadcrumbs} from '@/components/app/navigation';
import {Button} from '@/components/app/button';
import {ActionMenu} from '@/components/app/navigation';
import {ContentHints} from '@/components/app/hint';
import {cn} from '@/lib/utils';
import LogoDark from '../../../app/assets/img/logo-dark.webp';
import {MenuButton} from '../../../app/components/MenuButton';
import {useAppDispatch} from '../../../app/hooks/useAppDispatch';
import {useAppSelector} from '../../../app/hooks/useAppSelector';
import {useDevice} from '../../../app/hooks/useDevice';
import {getConfigValue} from '../../../app/utils/configUtils';
import {api} from '../../../app/services/api';
import {logout, selectSession} from '../../auth/authSlice';
import {AdditionalInfo} from '../../profile/components/AdditionalInfo';
import {ProfileImage} from '../../profile/components/ProfileImage';
import {PendingBiotwinSharesButton} from '../../biotwin-share/components/PendingBiotwinSharesButton';
import {useDemo} from '../../demo/hooks/useDemo';
import {useAppEventDispatch} from '../hooks/useAppEventDispatch';
import {GoToBiotwin} from './GoToBiotwin';
import {TenantSelect} from './TenantSelect';
import {BiotwinDropdown} from '../../biotwin/components/BiotwinDropdown';
import {NetworkStatusIcon} from './NetworkStatusIcon';
const excludedAdditionalIds: number[] = [9, 10];
export interface TopBarProps {
  className?: string;
}
export const TopBar = ({className}: TopBarProps) => {
  const matches = useMatches();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const session = useAppSelector(selectSession);
  const dispatchAppEvent = useAppEventDispatch();
  const {isDemo, showLimitReachedDialog} = useDemo();
  const {isMobile, isPortrait} = useDevice();

  // @ts-ignore
  const filteredMatches = matches.filter((match) => Boolean(match.handle?.title));
  const visibleAdditionalIds = useMemo(() => {
    return (
      session?.additionalIds
        .split(',')
        .filter(Boolean)
        .map(Number)
        .filter((additionalId) => !excludedAdditionalIds.includes(additionalId)) ?? []
    );
  }, [session?.additionalIds]);
  return (
    <div
      data-slot="app-navigation"
      className={cn(
        className,
        'sticky top-0 flex items-center bg-navigation text-navigation-foreground h-[4rem] px-2 md:px-4 shrink-0 shadow-lg z-5',
      )}
    >
      {/* The logo keeps its aspect ratio at full bar height; version and plan sit over its empty bottom edge. */}
      <Link className="relative h-12 shrink-0 text-navigation-foreground md:h-full" to="/biotwins">
        <img alt="Biotandem" className="h-full w-auto max-w-none py-1" src={LogoDark} />
        <span className="absolute bottom-0 left-0 mb-1 ml-4 flex gap-1 text-[0.5rem] leading-tight md:ml-5 md:pl-1 md:text-xs md:font-light">
          <span className="rounded-sm bg-background px-1 text-foreground">v{getConfigValue('uiVersion')}</span>
          {import.meta.env.MODE !== 'production' && (
            <span className="rounded-sm bg-background px-1 text-foreground capitalize">
              {isMobile && isPortrait ? import.meta.env.MODE[0] : import.meta.env.MODE}
            </span>
          )}
        </span>
        <span
          className="absolute right-0 bottom-0 mr-1 mb-px text-xs leading-tight font-bold uppercase md:text-sm"
          id="subscription-type"
        >
          {session?.subscriptionType}
          {visibleAdditionalIds.length > 0 && (
            <span className="absolute bottom-0 left-full ml-1">
              +
              <ContentHints side="bottom" target="#subscription-type">
                <ul className="m-0 pl-4">
                  {visibleAdditionalIds.map((additionalId, index) => (
                    <li className="py-1" key={`${additionalId}-${index}`}>
                      <AdditionalInfo additionalId={additionalId} />
                    </li>
                  ))}
                </ul>
              </ContentHints>
            </span>
          )}
        </span>
      </Link>
      <div className={'flex min-w-0 items-center gap-1 mr-auto'}>
        {/* @ts-ignore */}
        {matches[matches.length - 1].handle?.header !== false && (
          <Breadcrumbs
            className="hidden sm:flex bg-transparent border-0 text-sm ml-8"
            home={{
              onSelect: () => navigate('/biotwins'),
              icon: 'home',
              label: filteredMatches.length === 0 ? 'Biotwin List' : undefined,
            }}
            items={filteredMatches
              // @ts-ignore
              .map((match: any) => {
                return {
                  className: 'text-navigation-foreground',
                  onSelect: () => match.handle.pathname && navigate(match.handle.pathname),
                  label: match.handle.title,
                };
              })}
          />
        )}
        <BiotwinDropdown className={'hidden sm:block text-sm'} />
      </div>
      {session?.isPasswordChangeRequired !== 'true' && <GoToBiotwin />}
      {session?.isPasswordChangeRequired !== 'true' && session?.isAdmin === 'true' && (
        <Button
          className="hidden rounded-full md:inline-flex"
          onClick={() => navigate('/integration-simulator')}
          hint={'Integration Simulator'}
          variant="navigation"
          type="button"
          startIcon={<Icon icon={'code'} />}
          aria-label={'code'}
          size="icon"
        />
      )}
      {session?.isPasswordChangeRequired !== 'true' && session?.isAdmin === 'true' && (
        <Button
          className="hidden rounded-full md:inline-flex"
          onClick={() => navigate('/integration-demo')}
          hint={'Integration Demo'}
          variant="navigation"
          type="button"
          startIcon={<Icon icon={'desktop'} />}
          aria-label={'desktop'}
          size="icon"
        />
      )}
      {session?.isPasswordChangeRequired !== 'true' && session?.subscriptionIsValid === 'true' && (
        <PendingBiotwinSharesButton className="hidden rounded-full md:inline-flex" variant="navigation" type="button" />
      )}
      {!isDemo && (
        <MenuButton
          className="relative rounded-full"
          actions={[
            {
              onSelect: () => dispatchAppEvent('help'),
              icon: 'eye',
              label: 'Show Help',
            },
            {
              onSelect: () => window.open(`${getConfigValue('landingUrl')}/faq`, '_blank'),
              icon: 'external-link',
              label: 'Go To FAQs',
            },
            {
              onSelect: () => window.open(getConfigValue('landingUrl'), '_blank'),
              icon: 'globe',
              label: 'Go To Biotandem Web Site',
            },
          ]}
          variant="navigation"
          type="button"
          startIcon={<Icon icon={'question-circle'} />}
          aria-label={'question circle'}
          size="icon"
        />
      )}
      <div
        className={'border-l border-navigation-border mx-2 md:mx-4'}
        style={{
          height: '2.25rem',
        }}
      />
      <div className="flex shrink-0 items-center gap-2 h-full md:gap-4 md:px-4">
        <div>
          <span className="hidden md:block">{session?.name}</span>
          <TenantSelect />
        </div>
        <ActionMenu
          actions={[
            {
              onSelect: () => navigate('/integration-simulator'),
              icon: 'code',
              label: 'Integration Simulator',
              className: 'md:hidden',
              visible: session?.isPasswordChangeRequired !== 'true' && session?.isAdmin === 'true',
            },
            {
              onSelect: () => navigate('/integration-demo'),
              icon: 'desktop',
              label: 'Integration Demo',
              className: 'md:hidden',
              visible: session?.isPasswordChangeRequired !== 'true' && session?.isAdmin === 'true',
            },
            {
              onSelect: () => navigate('/admin'),
              icon: 'chart-pie',
              label: 'Admin Dashboard',
              visible:
                session?.isPasswordChangeRequired !== 'true' &&
                (session?.isAdmin === 'true' || session?.isTenantAdmin === 'true'),
            },
            {
              onSelect: () => navigate('/profile'),
              icon: 'user',
              label: 'User Profile',
              visible: session?.isPasswordChangeRequired !== 'true' && !isDemo,
            },
            {
              onSelect: () =>
                showLimitReachedDialog({
                  title: 'Unlock Full Access',
                }),
              icon: 'lock-open',
              label: 'Unlock full access',
              visible: session?.isPasswordChangeRequired !== 'true' && isDemo,
            },
            {
              separator: true,
              visible: session?.isPasswordChangeRequired !== 'true',
            },
            {
              onSelect: () => {
                navigate('/sign-in');
                dispatch(logout());
                dispatch(api.util.resetApiState());
              },
              icon: 'sign-out',
              label: 'Logout',
            },
          ]}
        >
          <button type="button" aria-label="Open account menu">
            <ProfileImage />
          </button>
        </ActionMenu>
      </div>

      <NetworkStatusIcon className={'mx-2'} isVisible={({isOffline, isSlow}) => isOffline || isSlow} />
    </div>
  );
};
